import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';

const QuickCreateSheet = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const studios = [
    {
      id: 'content',
      name: 'Content Studio V2',
      nameBM: 'Studio Kandungan V2',
      icon: '🎬',
      path: '/studio/content-v2',
      description: 'Create engaging content (9-step wizard)',
      descriptionBM: 'Cipta kandungan menarik (wizard 9-langkah)',
      available: true,
      locked: false
    },
    {
      id: 'live',
      name: 'Live Studio',
      nameBM: 'Studio Live',
      icon: '🎥',
      path: '/studio/live',
      description: 'Plan live events',
      descriptionBM: 'Rancang acara live',
      available: false,
      locked: true
    },
    {
      id: 'copy',
      name: 'Copywriting Studio',
      nameBM: 'Studio Copywriting',
      icon: '✍️',
      path: '/studio/copy',
      description: 'Write compelling copy',
      descriptionBM: 'Tulis salinan menarik',
      available: false,
      locked: true
    },
    {
      id: 'analyst',
      name: 'Analyst Studio',
      nameBM: 'Studio Analisis',
      icon: '📊',
      path: '/studio/analyst',
      description: 'Analyze trends and data',
      descriptionBM: 'Analisis trend dan data',
      available: false,
      locked: true
    }
  ];

  const handleStudioSelect = (studio) => {
    if (!studio.available) return;
    
    navigate(studio.path);
    onClose();
    
    // Add haptic feedback
    if (navigator.vibrate) {
      navigator.vibrate(10);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
      onClick={handleBackdropClick}
      data-testid="quick-create-backdrop"
    >
      {/* Sheet Container */}
      <div className={`
        fixed bottom-0 left-0 right-0 
        bg-white rounded-t-3xl 
        transform transition-transform duration-250 ease-out
        ${isOpen ? 'translate-y-0' : 'translate-y-full'}
        max-h-[70vh] overflow-y-auto
      `}>
        
        {/* Handle bar */}
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-10 h-1 bg-gray-300 rounded-full"></div>
        </div>

        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900" data-testid="quick-create-title">
                Quick Create
              </h2>
              <p className="text-sm text-gray-600">
                Choose a studio to start creating
              </p>
            </div>
            <Button
              onClick={onClose}
              variant="ghost"
              size="sm"
              className="text-gray-500 hover:text-gray-700"
              data-testid="quick-create-close"
            >
              ✕
            </Button>
          </div>
        </div>

        {/* Studios Grid */}
        <div className="p-6 pb-8">
          <div className="grid grid-cols-1 gap-4">
            {studios.map((studio) => (
              <Card
                key={studio.id}
                className={`
                  cursor-pointer transition-all duration-200 
                  ${studio.available 
                    ? 'hover:shadow-lg hover:border-blue-200 active:scale-95' 
                    : 'opacity-60 cursor-not-allowed'
                  }
                  ${studio.locked ? 'bg-gray-50 border-gray-200' : 'border-gray-200 hover:bg-blue-50'}
                `}
                onClick={() => handleStudioSelect(studio)}
                data-testid={`studio-${studio.id}-card`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center space-x-4">
                    {/* Studio Icon */}
                    <div className={`
                      flex items-center justify-center w-12 h-12 rounded-xl
                      ${studio.available ? 'bg-blue-100' : 'bg-gray-100'}
                    `}>
                      <span className="text-2xl">{studio.icon}</span>
                    </div>

                    {/* Studio Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className={`font-semibold ${studio.available ? 'text-gray-900' : 'text-gray-500'}`}>
                          {studio.name}
                        </h3>
                        {studio.locked && (
                          <Badge variant="secondary" className="text-xs bg-gray-200 text-gray-600">
                            Coming Soon
                          </Badge>
                        )}
                      </div>
                      <p className={`text-sm mt-0.5 ${studio.available ? 'text-gray-600' : 'text-gray-400'}`}>
                        {studio.description}
                      </p>
                    </div>

                    {/* Arrow or Lock Icon */}
                    {studio.available ? (
                      <div className="text-gray-400">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    ) : (
                      <div className="text-gray-400">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Safe area padding */}
        <div className="h-safe-area-inset-bottom bg-white"></div>
      </div>
    </div>
  );
};

export default QuickCreateSheet;